/**
 * Agent Inventory domain types. Field names and shapes must match the
 * backend contract exactly:
 *
 *   POST /v1/agents
 *   GET  /v1/agents?page=&limit=&sort=&agentType=&isOverPermissioned=&q=
 *   GET  /v1/agents/{id}
 *
 * `AgentInventoryItem` is the `data` shape returned by all three endpoints.
 */

export const AGENT_TYPE = {
  CODING_ASSISTANT: "coding_assistant",
  MCP_SERVER: "mcp_server",
  AGENT_BUILDER: "agent_builder",
  AUTOMATION_BOT: "automation_bot",
  OTHER: "other",
} as const;
export type AgentType = (typeof AGENT_TYPE)[keyof typeof AGENT_TYPE];

export const AGENT_TYPE_VALUES: readonly AgentType[] = [
  AGENT_TYPE.CODING_ASSISTANT,
  AGENT_TYPE.MCP_SERVER,
  AGENT_TYPE.AGENT_BUILDER,
  AGENT_TYPE.AUTOMATION_BOT,
  AGENT_TYPE.OTHER,
];

/** A registered agent, as returned by the Agent Inventory API. */
export interface AgentInventoryItem {
  id: string;
  name: string;
  agentType: AgentType;
  source: string;
  /** Who or what registered the agent (e.g. a user email, or a scanning integration name). Optional — not every source can attribute one. */
  registeredBy?: string;
  permissions: string[];
  dataClassesReachable: string[];
  /** Null until a blast-radius scoring pass has run for this agent. */
  blastRadiusScore: number | null;
  isOverPermissioned: boolean;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

/** Body for `POST /v1/agents`. */
export interface CreateAgentInput {
  name: string;
  agentType: AgentType;
  permissions: string[];
  dataClassesReachable: string[];
}

/**
 * Body for `PATCH /v1/agents/{id}`. Every field is optional — only
 * supplied fields are changed. `agentType` is deliberately not
 * editable: changing what an agent fundamentally *is* after discovery
 * is a re-registration, not an edit (matches the backend contract).
 */
export interface UpdateAgentInput {
  name?: string;
  permissions?: string[];
  dataClassesReachable?: string[];
  blastRadiusScore?: number | null;
}

/** Filter/search state for the agent list, independent of pagination and sort. */
export interface AgentInventoryFilters {
  q?: string;
  agentType?: AgentType;
  isOverPermissioned?: boolean;
}

export function hasActiveAgentInventoryFilters(filters: AgentInventoryFilters): boolean {
  return Boolean(
    filters.q || filters.agentType || filters.isOverPermissioned !== undefined,
  );
}

/** Column count, kept in sync with `AgentTable`'s `<thead>` so the loading `TableSkeleton` never drifts from the real table shape. */
export const AGENT_TABLE_COLUMN_COUNT = 6;
